import { QBankStructureLevelTag } from '@exam-management/components';
import {
  QBankStructureLevelCategory,
  QuestionGroupType,
  type QuestionStatus,
  QuestionTagCategory,
  useGetSingleQuestionQuestionGroupPropertiesByStatusAndLocationLazyQuery,
  useGetTagsByCategoryQuery,
} from '@exam-management/graphql';
import {
  Box,
  Chip,
  Divider,
  ExpandMoreRoundedIcon,
  Flex,
  IconButton,
  Rotate,
  Skeleton,
  styled,
  TableCell,
  TableRow,
  type TableRowProps,
  Text,
} from '@shared/components';
import { useEffect } from 'react';

import useQBankStructureDashboardSearchParams from '../../hooks/use-qbank-structure-dashboard-search-params/use-qbank-structure-dashboard-search-params';
import { QBS_LEVEL_TAG_BG_COLOR_BY_CATEGORY } from '../qbank-structure-level-item-list/qbank-structure-level-item';
import { type QBankStructureSingleQuestionTableData } from '../qbank-structure-single-question-dashboard-table';

import { formatDataToQuestionSummaryWithMap } from './utils';

function renderBox(totalBoxes: number) {
  return (
    <>
      {Array.from({ length: totalBoxes }).map((_, index) => (
        <Box
          key={`box-${index}`}
          sx={{
            minWidth: '16px',
            borderRight: (theme) => `1px solid ${theme.palette.grey[300]}`,
          }}
        />
      ))}
    </>
  );
}

interface DashboardSingleSubTopicTableData
  extends Omit<QBankStructureSingleQuestionTableData, 'level'> {
  level: QBankStructureLevelCategory;
}

interface DashboardSingleSubTopicTableRowProps extends TableRowProps {
  data: DashboardSingleSubTopicTableData;
  expanded?: boolean;
  onExpand?: (id: string) => void;
  totalBoxes?: number;
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  borderColor: theme.palette.grey[300],
  borderRight: `1px solid ${theme.palette.grey[300]}`,
  ...theme.typography.body2Semibold,
}));

function SummaryCell({
  value,
  isPart,
}: Readonly<{
  value: number | string | null | undefined;
  isPart: boolean;
}>) {
  return (
    <StyledTableCell
      align='center'
      sx={(theme) => ({ color: isPart ? theme.palette.common.white : undefined })}
    >
      {value ?? '-'}
    </StyledTableCell>
  );
}

function SkeletonCell({ isPart }: Readonly<{ isPart: boolean }>) {
  return (
    <StyledTableCell
      align='center'
      sx={(theme) => ({
        color: isPart ? theme.palette.common.white : undefined,
      })}
    >
      <Skeleton variant='text' width={35} sx={{ justifySelf: 'center' }} />
    </StyledTableCell>
  );
}

function QuestionTypeChip({
  questionGroupType,
}: Readonly<{ questionGroupType: QuestionGroupType }>) {
  return (
    <Chip
      label={
        <Text variant='captionSemibold'>
          {questionGroupType === QuestionGroupType.Single ? 'Single' : 'LOQ'}
        </Text>
      }
      size='small'
      sx={(theme) => ({
        backgroundColor:
          questionGroupType === QuestionGroupType.Single
            ? theme.palette.primary.light
            : theme.palette.success.light,
        minWidth: '62px',
      })}
    />
  );
}

const QUESTION_TYPES_ROW = [QuestionGroupType.Single, QuestionGroupType.LotOfQuestion];

export default function DashboardSingleSubTopicTableRow(
  props: Readonly<DashboardSingleSubTopicTableRowProps>,
) {
  const { data, expanded: isExpanded = false, onExpand, totalBoxes = 0, ...restProps } = props;
  const { id, levelName, level, calculate, nonCalculate, order } = data;
  const isPart = level === QBankStructureLevelCategory.Part;
  const expandedBgColor = QBS_LEVEL_TAG_BG_COLOR_BY_CATEGORY[level];

  const [{ status }] = useQBankStructureDashboardSearchParams();

  const calcSummary = (
    calculate?: {
      easyQuestions: number | null;
      mediumQuestions: number | null;
      hardQuestions: number | null;
    },
    nonCalculate?: {
      easyQuestions: number | null;
      mediumQuestions: number | null;
      hardQuestions: number | null;
    },
  ) =>
    (calculate?.easyQuestions ?? 0) +
    (calculate?.mediumQuestions ?? 0) +
    (calculate?.hardQuestions ?? 0) +
    (nonCalculate?.easyQuestions ?? 0) +
    (nonCalculate?.mediumQuestions ?? 0) +
    (nonCalculate?.hardQuestions ?? 0);
  const [
    getSingleQuestionQuestionGroupProperties,
    { data: singleQuestionQuestionGroupProperties, loading },
  ] = useGetSingleQuestionQuestionGroupPropertiesByStatusAndLocationLazyQuery();

  const summary = calcSummary(calculate, nonCalculate);
  const displaySummary = summary === 0 ? '-' : summary;

  const { data: diffTagsRes } = useGetTagsByCategoryQuery({
    variables: { input: { category: QuestionTagCategory.Difficulty } },
  });

  const { data: calcTagsRes } = useGetTagsByCategoryQuery({
    variables: { input: { category: QuestionTagCategory.Calculation } },
  });

  const difficultyTags = diffTagsRes?.qbsGetTagsByCategory ?? [];
  const calculationTags = calcTagsRes?.qbsGetTagsByCategory ?? [];

  useEffect(() => {
    if (!isExpanded) return;
    getSingleQuestionQuestionGroupProperties({
      variables: {
        input: { qBankStructureLevelItemIds: [id], status: status as QuestionStatus },
      },
    });
  }, [isExpanded, id]);

  const singleGroup =
    singleQuestionQuestionGroupProperties?.qctGetSingleQuestionQuestionGroupPropertiesByStatusAndLocation.find(
      (item) => item.locationId === id,
    );

  return (
    <>
      <TableRow
        {...restProps}
        sx={{
          border: 'none',
          ':last-child td': { borderBottom: 'none' },
          backgroundColor: expandedBgColor,
          ...restProps.sx,
        }}
      >
        <StyledTableCell sx={(theme) => ({ p: theme.spacing(0), borderBottom: 'none' })}>
          <Flex sx={{ minHeight: '48px', backgroundColor: 'white' }}>
            {renderBox(totalBoxes)}

            <Flex
              sx={(theme) => ({
                flex: 1,
                alignItems: 'center',
                backgroundColor: expandedBgColor,
                borderBottom: '1px solid ' + theme.palette.grey[300],
              })}
            >
              <Flex sx={{ minWidth: '48px', justifyContent: 'center' }}>
                <IconButton onClick={() => onExpand?.(id)}>
                  <Rotate in={isExpanded}>
                    <ExpandMoreRoundedIcon />
                  </Rotate>
                </IconButton>
              </Flex>

              <Flex sx={(theme) => ({ flex: 1, p: theme.spacing(2), gap: theme.spacing(2) })}>
                <QBankStructureLevelTag category={level} />
                <Text>{order}</Text>
                <Divider orientation='vertical' flexItem />
                <Text>{levelName}</Text>
              </Flex>
            </Flex>
          </Flex>
        </StyledTableCell>

        <SummaryCell value={calculate?.easyQuestions} isPart={isPart} />
        <SummaryCell value={calculate?.mediumQuestions} isPart={isPart} />
        <SummaryCell value={calculate?.hardQuestions} isPart={isPart} />
        <SummaryCell value={nonCalculate?.easyQuestions} isPart={isPart} />
        <SummaryCell value={nonCalculate?.mediumQuestions} isPart={isPart} />
        <SummaryCell value={nonCalculate?.hardQuestions} isPart={isPart} />
        <SummaryCell value={displaySummary} isPart={isPart} />
      </TableRow>

      {QUESTION_TYPES_ROW.map((groupType) => {
        const isSingle = groupType === QuestionGroupType.Single;

        if (loading) {
          return (
            <TableRow {...restProps} key={`${id}-${groupType}`} sx={{ backgroundColor: 'white' }}>
              <StyledTableCell sx={{ p: 0, borderBottom: 'none' }}>
                <Flex sx={{ minHeight: '48px', backgroundColor: 'white' }}>
                  {renderBox(totalBoxes + 1)}
                  <Flex
                    sx={(theme) => ({
                      flex: 1,
                      alignItems: 'center',
                      borderBottom: '1px solid ' + theme.palette.grey[300],
                    })}
                  >
                    <Flex sx={(theme) => ({ flex: 1, p: theme.spacing(2), gap: theme.spacing(2) })}>
                      <Box sx={{ minWidth: '48px' }} />

                      <Text variant='body2Semibold'>รูปแบบข้อสอบ:</Text>
                      <QuestionTypeChip questionGroupType={groupType} />
                    </Flex>
                  </Flex>
                </Flex>
              </StyledTableCell>

              {(() => {
                const cells = [];
                for (let i = 0; i < 7; i++) {
                  cells.push(<SkeletonCell key={i} isPart={isPart} />);
                }
                return cells;
              })()}
            </TableRow>
          );
        }

        if (!isExpanded || !singleGroup) return null;

        const questionGroup = singleGroup.questionGroupProperties.find(
          (item) => item.questionGroupType === groupType,
        );

        const formatted = formatDataToQuestionSummaryWithMap({
          questionGroups: questionGroup?.questionProperties,
          difficultyTags,
          calculationTags,
        });

        let totalSummary = '-';
        if (questionGroup) {
          totalSummary = isSingle
            ? `${formatted.total}`
            : `${questionGroup?.totalQuestionGroups}(${formatted.total})`;
        }

        return (
          <TableRow {...restProps} key={`${id}-${groupType}`} sx={{ backgroundColor: 'white' }}>
            <StyledTableCell sx={(theme) => ({ p: theme.spacing(0), borderBottom: 'none' })}>
              <Flex sx={{ minHeight: '48px', backgroundColor: 'white' }}>
                {renderBox(totalBoxes + 1)}

                <Flex
                  sx={(theme) => ({
                    flex: 1,
                    alignItems: 'center',
                    borderBottom: '1px solid ' + theme.palette.grey[300],
                  })}
                >
                  <Flex
                    sx={(theme) => ({
                      flex: 1,
                      p: theme.spacing(2),
                      gap: theme.spacing(2),
                    })}
                  >
                    <Box sx={{ minWidth: '48px' }} />
                    <Text variant='body2Semibold'>รูปแบบข้อสอบ:</Text>
                    <QuestionTypeChip questionGroupType={groupType} />
                  </Flex>
                </Flex>
              </Flex>
            </StyledTableCell>

            <SummaryCell value={formatted.calculate?.easyQuestions} isPart={isPart} />
            <SummaryCell value={formatted.calculate?.mediumQuestions} isPart={isPart} />
            <SummaryCell value={formatted.calculate?.hardQuestions} isPart={isPart} />
            <SummaryCell value={formatted.nonCalculate?.easyQuestions} isPart={isPart} />
            <SummaryCell value={formatted.nonCalculate?.mediumQuestions} isPart={isPart} />
            <SummaryCell value={formatted.nonCalculate?.hardQuestions} isPart={isPart} />

            <SummaryCell value={totalSummary} isPart={isPart} />
          </TableRow>
        );
      })}
    </>
  );
}
